import React, { useState, useEffect, useRef } from 'react';
import { useToast } from '../../../components/ui/Toast';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { profileService } from '../../../services/profileService';
import conferenceService from '../../../services/conferenceService';

/**
 * Reviewer directory search for the Team tab. Chairs search opted-in reviewers
 * by name / institution / expertise keyword and add them to the conference.
 */
const ReviewerSearch = ({ conferenceId, existingReviewerIds = [], onAdded }) => {
  // Replaces toastError(): non-blocking and styled; errors persist until
  // dismissed rather than vanishing on a timer.
  const { error: toastError } = useToast();
  const { t } = useTranslation('conferences');
  const k = (key, opts) => t(`managementDashboard.team.reviewerSearch.${key}`, opts);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [addedIds, setAddedIds] = useState([]);
  const debounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); setTouched(false); return; }
    setLoading(true);
    setTouched(true);
    debounceRef.current = setTimeout(async () => {
      try {
        setResults(await profileService.searchReviewers(query));
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const isOnTeam = (id) => existingReviewerIds?.includes(id) || addedIds?.includes(id);

  const handleAdd = async (reviewer) => {
    setAddingId(reviewer?.id);
    try {
      await conferenceService?.addConferenceRole(conferenceId, reviewer?.id, 'reviewer');
      setAddedIds(prev => [...prev, reviewer?.id]);
      onAdded?.();
    } catch (err) {
      toastError(err?.message || k('addFailed'));
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div className="bg-muted/30 rounded-md border border-border p-3">
      <h3 className="text-xs font-semibold text-foreground mb-1 flex items-center gap-1.5">
        <Icon name="Search" size={13} className="text-primary" />
        {k('title')}
      </h3>
      <p className="text-xs text-muted-foreground mb-2">{k('subtitle')}</p>

      <div className="relative">
        <Icon name="Search" size={14} className="absolute ltr:left-3 rtl:right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e?.target?.value)}
          placeholder={k('placeholder')}
          className="w-full h-10 rounded-md border border-input bg-background ltr:pl-9 rtl:pr-9 px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
        {loading && (
          <Icon name="Loader2" size={14} className="absolute ltr:right-3 rtl:left-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />
        )}
      </div>

      {/* Results */}
      {query.trim() && (
        <div className="mt-2">
          {loading ? null : results?.length === 0 && touched ? (
            <p className="text-xs text-muted-foreground py-3 text-center">{k('empty')}</p>
          ) : (
            <div className="max-h-72 overflow-y-auto divide-y divide-border border border-border rounded-md bg-card">
              {results?.map((r) => {
                const onTeam = isOnTeam(r?.id);
                return (
                  <div key={r?.id} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="w-9 h-9 overflow-hidden bg-muted flex items-center justify-center flex-shrink-0 border border-border">
                      {r?.avatarUrl ? (
                        <img src={r?.avatarUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Icon name="User" size={16} className="text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{r?.firstName} {r?.lastName}</p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {[r?.institution, r?.email]?.filter(Boolean)?.join(' · ')}
                      </p>
                      {r?.keywords?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {r?.keywords?.slice(0, 5)?.map((kw, i) => (
                            <span key={i} className="px-1.5 py-0.5 text-[10px] bg-primary/10 text-primary">{kw}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    {onTeam ? (
                      <span className="text-[11px] font-medium text-success flex items-center gap-1 flex-shrink-0">
                        <Icon name="Check" size={12} /> {addedIds?.includes(r?.id) ? k('added') : k('onTeam')}
                      </span>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleAdd(r)}
                        loading={addingId === r?.id}
                        iconName="UserPlus"
                        iconPosition="left"
                        className="flex-shrink-0"
                      >
                        {k('add')}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewerSearch;
